import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { FormItem } from '@adsui/form-item';
import { BudgetTierTitles, CustomBudget } from './model/constants';

const EM_DASH = '\u2014';

// Resolves the localized title for a selected budget-suggestion tier. The
// custom-budget tier ("Set your own budget") isn't in BudgetTierTitles, so map
// it explicitly. Returns undefined for an absent/unknown tier so the summary
// falls back to the amount-only ("$250/day") form used in custom mode.
const getBudgetTierTitle = (tier, i18n) => {
  if (!tier) {
    return undefined;
  }
  if (tier === CustomBudget) {
    return i18n.getString(_TL_('Custom budget'));
  }
  const title = BudgetTierTitles[tier];
  return title ? i18n.getString(title) : undefined;
};

// Formats a daily budget amount using the currency's NARROW symbol ("$50.00")
// rather than the default CLDR symbol some non-US English locales use ("US$ 50.00").
// The i18n model is Globalize-based, which has no narrowSymbol currency style, so
// build the string from the number-only currency format (keeps per-currency decimal
// rules and grouping) prefixed with the narrow symbol.
const formatDailyAmount = (amount, i18n, currency) => {
  const numberOnly = i18n.formatCurrency(amount, currency, { withSymbol: false });
  const getSymbol = i18n.getCurrencySymbol;
  const symbol = typeof getSymbol === 'function'
    ? (getSymbol.call(i18n, currency, { alt: 'narrow' }) || getSymbol.call(i18n, currency) || '')
    : '';
  return `${symbol}${numberOnly}`;
};

// Builds the read-only summary string for a campaign daily budget.
// - When a suggestion tier was selected: "$250/day Recommended" (tier is one of
//   Growth, Recommended, Basic, or Custom budget).
// - In custom mode (no tier, e.g. the settings Budget row): "$250/day".
// Returns an em-dash when no amount is set.
export const getBudgetSuggestionSummary = (budget, tier, i18n, currency) => {
  const amount = budget && budget.Amount;
  if (amount === undefined || amount === null || amount === '') {
    return EM_DASH;
  }
  const perDay = i18n.getString(_TL_('{{amount}}/day'), {
    amount: formatDailyAmount(amount, i18n, currency),
  });
  const tierTitle = getBudgetTierTitle(tier, i18n);
  return tierTitle
    ? i18n.getString(_TL_('{{budget}} {{tier}}'), { budget: perDay, tier: tierTitle })
    : perDay;
};

// Read-only view of the campaign budget. Reads the budget amount from `field` and
// the (optional) selected tier from the sibling `tierField`, both off the
// surrounding FormGroup, and renders a single-line summary (e.g. "$250/day" or
// "$250/day Recommended"). The tier lives beside the Budget entity (not inside it)
// because it's a UI-only hint that never reaches save. Sibling read-only component
// to the editable BudgetSuggestion.
export const BudgetSuggestionReadOnly = ({ field, tierField, className }) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const currency = (serverContext && serverContext.CurrentAccount && serverContext.CurrentAccount.CurrencyCode) || 'USD';

  return (
    <FormItem field={field}>
      {slice => (
        <FormItem field={tierField}>
          {tier => (
            <span
              className={className}
              data-testid="budget-suggestion-read-only"
            >
              {getBudgetSuggestionSummary(slice, tier, i18n, currency)}
            </span>
          )}
        </FormItem>
      )}
    </FormItem>
  );
};

BudgetSuggestionReadOnly.propTypes = {
  field: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  tierField: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  className: PropTypes.string,
};

BudgetSuggestionReadOnly.defaultProps = {
  field: 'Campaign/Budget',
  tierField: 'Campaign/BudgetTier',
  className: undefined,
};

BudgetSuggestionReadOnly.displayName = 'BudgetSuggestionReadOnly';
